import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { listarChamados, type FiltroDeChamados } from '@/portal/portal.api';
import { dataCurta, rotuloDeSituacao } from '@/portal/formato';

/** A lista dos chamados do cliente — a primeira tela depois de entrar. */
export function ChamadosPage() {
  const [situacao, setSituacao] = useState<FiltroDeChamados['situacao']>('abertos');

  const consulta = useInfiniteQuery({
    queryKey: ['portal', 'chamados', situacao],
    queryFn: ({ pageParam }) =>
      listarChamados({ situacao: situacao ?? 'abertos', cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = consulta.data?.pages.flatMap((p) => p.items) ?? [];

  function trocarFiltro(opcao: FiltroDeChamados['situacao']) {
    setSituacao(opcao);
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Meus chamados</h1>
        <div className="flex gap-1" role="group" aria-label="Filtrar por situação">
          {(['abertos', 'resolvidos', 'todos'] as const).map((opcao) => (
            <button
              key={opcao}
              type="button"
              onClick={() => trocarFiltro(opcao)}
              aria-pressed={situacao === opcao}
              className={
                situacao === opcao
                  ? 'rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground'
                  : 'rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent'
              }
            >
              {rotuloDeSituacao(opcao)}
            </button>
          ))}
        </div>
      </div>

      {consulta.isPending ? <p className="text-sm text-muted-foreground">Carregando…</p> : null}

      {!consulta.isPending && itens.length === 0 ? (
        <EmptyState
          title="Nenhum chamado por aqui"
          description="Quando você abrir um chamado, ele aparece nesta lista."
        />
      ) : null}

      <ul className="space-y-2">
        {itens.map((chamado) => (
          <li key={chamado.id}>
            <Card className="transition-shadow hover:shadow-card-hover">
              <Link to="/chamados/$id" params={{ id: chamado.id }} className="block px-5 py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{chamado.numero}</span>
                  <span className="rounded-full border border-border px-2 py-0.5 text-xs">
                    {chamado.situacao}
                  </span>
                </div>
                <p className="mt-1 font-medium">{chamado.assunto}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {chamado.servico ? `${chamado.servico.nome} · ` : ''}
                  aberto em {dataCurta(chamado.abertoEm)}
                  {chamado.prazoEm ? ` · prazo ${dataCurta(chamado.prazoEm)}` : ''}
                </p>
              </Link>
            </Card>
          </li>
        ))}
      </ul>

      {consulta.hasNextPage ? (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => void consulta.fetchNextPage()}
            disabled={consulta.isFetchingNextPage}
            className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
